import {
  faChevronDown,
  faChevronRight
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useColorScheme
} from '@mui/material';
import { useAtom } from 'jotai';
import React, { useCallback, useEffect, useRef } from 'react';

import { categoryAtom, sourceDialogModeAtom } from '../SourceCreate.store';
import useStyles from '../SourceCreate.styles';
import { SourceDialogMode } from '../SourceCreate.types';
import type {
  Category,
  HierarchicalMultiSelectProps,
  SubCategory
} from './HierarchicalMultiSelect.types';
import transformCategories from './HierarchicalMultiSelect.utils';

/**
 * HierarchicalMultiSelect - Multi-level category selector for Google Workspace
 *
 * Displays a two-tier hierarchy: Alert Center API and Audit Reports API,
 * each with expandable subcategories. Supports select-all and individual selection.
 *
 * @param value - Array of selected subcategory IDs
 * @param onChange - Callback when selection changes
 */

const HierarchicalMultiSelect: React.FC<HierarchicalMultiSelectProps> = ({
  value = null,
  onChange
}: HierarchicalMultiSelectProps) => {
  const { mode: themeMode } = useColorScheme();
  const { classes } = useStyles({ themeMode });
  const [expandedCategories, setExpandedCategories] = React.useState<
    Set<string>
  >(new Set());
  const [categoryMasterList] = useAtom(categoryAtom);
  const categories = transformCategories(categoryMasterList);
  const [mode] = useAtom(sourceDialogModeAtom);
  // Master list for which auto-select already ran (re-runs when switching sources)
  const autoSelectedForRef = useRef<typeof categoryMasterList | null>(null);

  // Normalize value to always be an array
  const normalizedValue: string[] = value || [];

  // Auto-select all categories and subcategories by default
  useEffect(() => {
    if (
      mode === SourceDialogMode.New &&
      categories.length > 0 &&
      autoSelectedForRef.current !== categoryMasterList &&
      onChange
    ) {
      const allSubcategoryIds = categories.flatMap((cat) =>
        cat.subcategories.map((sub) => sub.id)
      );
      onChange(allSubcategoryIds);
      autoSelectedForRef.current = categoryMasterList;
    }
  }, [mode, categories, categoryMasterList, onChange]);

  // Handle category change with proper type conversion
  const handleCategoryChange = useCallback(
    (newValue: string | string[]) => {
      const selectedCategories = Array.isArray(newValue)
        ? newValue
        : newValue.split(',');

      if (onChange) {
        onChange(selectedCategories);
      }
    },
    [onChange]
  );

  // Helper function to get subcategories for a category
  const getSubcategoriesForCategory = (
    categoryLabel: string
  ): SubCategory[] => {
    const category = categories.find(
      (cat: Category) => cat.label === categoryLabel
    );
    return category
      ? category.subcategories.map((sub: SubCategory) => ({
          ...sub,
          type: 'subcategory' as const
        }))
      : [];
  };

  // Helper function to get category selection state
  const getCategorySelectionState = (categoryLabel: string) => {
    const subcategories = getSubcategoriesForCategory(categoryLabel);
    const selectedSubcategories = subcategories.filter((sub) =>
      normalizedValue.includes(sub.id)
    );
    const allSelected =
      subcategories.length === selectedSubcategories.length &&
      subcategories.length > 0;
    const someSelected =
      selectedSubcategories.length > 0 &&
      selectedSubcategories.length < subcategories.length;

    return {
      allSelected,
      someSelected,
      selectedCount: selectedSubcategories.length,
      totalCount: subcategories.length
    };
  };

  // Toggle category expansion
  const toggleCategory = (categoryLabel: string) => {
    setExpandedCategories((prev: Set<string>) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryLabel)) {
        newSet.delete(categoryLabel);
      } else {
        newSet.add(categoryLabel);
      }
      return newSet;
    });
  };

  // Handle category checkbox click (select/deselect all)
  const handleCategoryCheckboxClick = (
    categoryLabel: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.stopPropagation();

    const { allSelected } = getCategorySelectionState(categoryLabel);
    const subcategories = getSubcategoriesForCategory(categoryLabel);

    if (allSelected) {
      // Deselect all subcategories from this category
      const subcategoryIds = subcategories.map((sub: SubCategory) => sub.id);
      const newSelected = normalizedValue.filter(
        (id: string) => !subcategoryIds.includes(id)
      );
      handleCategoryChange(newSelected);
    } else {
      // Select all subcategories from this category
      const subcategoryIds = subcategories.map((sub: SubCategory) => sub.id);
      const newSelected = [...new Set([...normalizedValue, ...subcategoryIds])];
      handleCategoryChange(newSelected);
    }
  };

  // Handle individual subcategory checkbox click
  const handleSubcategoryCheckboxClick = (subcategory: SubCategory) => {
    const isSelected = normalizedValue.includes(subcategory.id);

    if (isSelected) {
      const newSelected = normalizedValue.filter(
        (id: string) => id !== subcategory.id
      );
      handleCategoryChange(newSelected);
    } else {
      const newSelected = [...normalizedValue, subcategory.id];
      handleCategoryChange(newSelected);
    }
  };

  return (
    <Box>
      <TableContainer className={classes.dpTableContainer}>
        <Table className={classes.dpTable}>
          <TableBody>
            {categories.map((category: Category) => {
              const { allSelected, someSelected, selectedCount, totalCount } =
                getCategorySelectionState(category.label);
              const subcategories = getSubcategoriesForCategory(category.label);
              const isExpanded = expandedCategories.has(category.label);

              return (
                <React.Fragment key={category.id}>
                  {/* Category Header Row */}
                  <TableRow>
                    <TableCell className={classes.dsHeaderTableCell1}>
                      <IconButton
                        aria-label={
                          isExpanded
                            ? `Collapse ${category.label}`
                            : `Expand ${category.label}`
                        }
                        onClick={() => toggleCategory(category.label)}
                        size="small"
                        sx={{ p: 0.5 }}
                      >
                        <FontAwesomeIcon
                          icon={isExpanded ? faChevronDown : faChevronRight}
                          style={{ fontSize: '0.9rem' }}
                        />
                      </IconButton>

                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, flexGrow: 1 }}
                      >
                        {category.label}
                      </Typography>

                      {selectedCount > 0 && (
                        <Typography
                          className={classes.selectedCountText}
                          variant="body1"
                        >
                          Selected ({selectedCount}/{totalCount})
                        </Typography>
                      )}
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleCategoryCheckboxClick(category.label, e)
                        }
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>

                  {/* Collapsible Subcategories Row */}
                  <TableRow className={classes.CollapseTableRow}>
                    <TableCell>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Stack
                          spacing={0.5}
                          sx={{
                            // pl: 6,
                            height: '9.5rem',
                            overflowY: 'auto'
                          }}
                        >
                          {subcategories.map((subcategory) => {
                            const isSelected = normalizedValue.includes(
                              subcategory.id
                            );

                            return (
                              <FormControlLabel
                                key={subcategory.id}
                                labelPlacement="start"
                                control={
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() =>
                                      handleSubcategoryCheckboxClick(
                                        subcategory
                                      )
                                    }
                                    size="small"
                                  />
                                }
                                label={
                                  <Typography variant="body2">
                                    {subcategory.label}
                                  </Typography>
                                }
                                sx={{
                                  margin: 0,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  '& .MuiFormControlLabel-label': {
                                    width: '100%'
                                  }
                                }}
                              />
                            );
                          })}
                        </Stack>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default HierarchicalMultiSelect;
